import { z } from 'zod'

/**
 * The Customer.io transactional message identifiers the event confirmation
 * emails send with, read and written as platform configuration.
 *
 * All the identifiers live in one configuration value so a school overriding
 * one keeps inheriting the others. Clearing a field means REMOVING it from the
 * stored object, never writing an empty string: an empty string wins the
 * platform/school merge and would take that school off email entirely rather
 * than returning it to the platform default.
 */

/**
 * A partial write. Either field may be sent alone; the other is left untouched.
 * `null` clears that field (removing the key), returning the school to the
 * platform default. Omitting a field entirely leaves it unchanged.
 */
export const PutEventEmailMessageIdsRequestSchema = z
  .object({
    rsvpConfirmationMessageId: z.string().trim().min(1).nullable().optional(),
    paidReceiptMessageId: z.string().trim().min(1).nullable().optional(),
    /** The slot-cancelled notice (plan 2026-09-18-0147, U7 — R19, KTD12). */
    slotCancelledMessageId: z.string().trim().min(1).nullable().optional(),
    /** One registration's ticket, resent by an admin (plan 2026-09-09-1309, U7 — R12). */
    admissionTicketMessageId: z.string().trim().min(1).nullable().optional(),
    /** The event-cancelled notice (plan 2026-09-09-1309, U12 — R26). */
    eventCancelledMessageId: z.string().trim().min(1).nullable().optional(),
    /** A waitlist offer, sent when a released seat is held for the waitlist (plan 2026-09-09-1309, U13 — R39). */
    waitlistOfferMessageId: z.string().trim().min(1).nullable().optional()
  })
  .refine(
    b =>
      b.rsvpConfirmationMessageId !== undefined ||
      b.paidReceiptMessageId !== undefined ||
      b.slotCancelledMessageId !== undefined ||
      b.admissionTicketMessageId !== undefined ||
      b.eventCancelledMessageId !== undefined ||
      b.waitlistOfferMessageId !== undefined,
    { message: 'Provide at least one identifier field' }
  )

export type PutEventEmailMessageIdsRequest = z.infer<typeof PutEventEmailMessageIdsRequestSchema>

/**
 * Provenance, not just the resolved value.
 *
 * An admin who sees only a resolved value and edits it is one save away from
 * overwriting the platform default while believing they are editing their own
 * school's. The three values are read from the two rows independently rather
 * than inferred by comparison — a school override that happens to equal the
 * platform value is still an override, and a comparison-based implementation
 * reports it as inherited.
 */
export const EventEmailMessageIdProvenanceSchema = z.object({
  platform: z.string().nullable(),
  school: z.string().nullable(),
  resolved: z.string().nullable(),
  /** True when the school row supplies this field, even if it equals the platform value. */
  overridden: z.boolean()
})

export const EventEmailMessageIdsResponseSchema = z.object({
  rsvpConfirmation: EventEmailMessageIdProvenanceSchema,
  paidReceipt: EventEmailMessageIdProvenanceSchema,
  slotCancelled: EventEmailMessageIdProvenanceSchema,
  admissionTicket: EventEmailMessageIdProvenanceSchema,
  eventCancelled: EventEmailMessageIdProvenanceSchema,
  waitlistOffer: EventEmailMessageIdProvenanceSchema
})

export type EventEmailMessageIdProvenance = z.infer<typeof EventEmailMessageIdProvenanceSchema>
export type EventEmailMessageIdsResponse = z.infer<typeof EventEmailMessageIdsResponseSchema>
